import React from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, Ban, Check, Loader2, Image as ImageIcon } from 'lucide-react';

export default function VirtualBackgroundSelector({
  isOpen,
  onClose,
  selectedBgId,
  defaultBgId,
  onSelectBackground,
  availableBackgrounds = [],
  isApplying = false,
  isDark = true,
  isCameraOn = true,
}) {
  if (!isOpen) return null;

  const activeDefaultId = defaultBgId || availableBackgrounds.find((b) => b.isDefault)?.id || 'none';
  const isPortraitViewport = typeof window !== 'undefined' && window.innerHeight > window.innerWidth;

  const content = (
    <div
      className={`fixed inset-0 z-[200000] ${isDark ? 'bg-slate-950/80' : 'bg-slate-900/60'} backdrop-blur-md flex items-end sm:items-center justify-center p-3 sm:p-4`}
      style={{ zIndex: 200000 }}
      onClick={onClose}
    >
      <div
        className={`${
          isDark ? 'bg-slate-900 border-slate-800 text-white shadow-black/80' : 'bg-white border-slate-200 text-slate-900 shadow-slate-300/50'
        } border rounded-3xl p-5 sm:p-6 max-w-md w-full shadow-2xl relative animate-in zoom-in-95 duration-200 text-left mb-16 sm:mb-0`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-3.5 mb-3.5 border-b border-inherit">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-md shadow-emerald-500/30">
              <Sparkles className="w-5 h-5 text-amber-200" />
            </div>
            <div>
              <h3 className="text-base font-bold tracking-tight">Virtual Background</h3>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Choose your camera background
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-2 rounded-xl transition ${
              isDark ? 'hover:bg-slate-800 text-slate-400 hover:text-white' : 'hover:bg-slate-100 text-slate-500 hover:text-slate-900'
            }`}
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!isCameraOn && (
          <div className={`mb-3.5 px-3 py-2.5 rounded-xl text-xs flex items-center gap-2 ${
            isDark ? 'bg-amber-500/15 border border-amber-500/30 text-amber-300' : 'bg-amber-50 border border-amber-200 text-amber-700'
          }`}>
            <ImageIcon className="w-4 h-4 shrink-0" />
            <span>Camera is off. Your choice will apply when camera turns on.</span>
          </div>
        )}

        {/* 3 Main Options Grid: Office Studio, Blur, None */}
        <div className="grid grid-cols-3 gap-3">
          {availableBackgrounds.map((bg) => {
            const isSelected = selectedBgId === bg.id;
            const isCurrentlyApplying = isApplying && isSelected;
            const thumbSrc = isPortraitViewport && bg.portraitPath ? bg.portraitPath : bg.thumbnail || bg.landscapePath;

            return (
              <button
                type="button"
                key={bg.id}
                onClick={() => onSelectBackground(bg.id)}
                disabled={isApplying}
                className={`group relative flex flex-col items-center rounded-2xl border overflow-hidden p-1.5 transition-all text-left ${
                  isSelected
                    ? 'border-emerald-500 ring-2 ring-emerald-500/50 shadow-lg shadow-emerald-500/20'
                    : isDark
                      ? 'border-slate-800 hover:border-slate-700 bg-slate-800/40 hover:bg-slate-800/80'
                      : 'border-slate-200 hover:border-slate-300 bg-slate-50 hover:bg-slate-100'
                }`}
              >
                {/* Preview Box */}
                <div
                  className={`w-full aspect-video rounded-xl overflow-hidden flex items-center justify-center relative ${
                    isDark ? 'bg-slate-800' : 'bg-slate-200'
                  }`}
                >
                  {bg.type === 'none' && (
                    <div className="flex flex-col items-center justify-center gap-1 text-slate-400 group-hover:text-slate-300">
                      <Ban className="w-6 h-6" />
                    </div>
                  )}

                  {bg.type === 'blur' && (
                    <div className="w-full h-full bg-gradient-to-br from-emerald-500/30 via-teal-500/30 to-emerald-500/30 backdrop-blur-md flex flex-col items-center justify-center text-emerald-400">
                      <Sparkles className="w-6 h-6" />
                    </div>
                  )}

                  {bg.type === 'image' && (
                    <img
                      src={thumbSrc}
                      alt={bg.label}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  )}

                  {/* Applying loader */}
                  {isCurrentlyApplying && (
                    <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center text-white">
                      <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
                    </div>
                  )}

                  {/* Checkmark */}
                  {isSelected && !isCurrentlyApplying && (
                    <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-md">
                      <Check className="w-3 h-3 stroke-[3]" />
                    </div>
                  )}

                  {/* Default Pill on designated background */}
                  {(bg.id === activeDefaultId || bg.isDefault) && !isSelected && (
                    <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded-md bg-slate-900/80 backdrop-blur-xs text-[9px] font-semibold text-slate-200">
                      Default
                    </div>
                  )}
                </div>

                {/* Title */}
                <div className="w-full mt-2 px-1 text-center">
                  <span
                    className={`block text-xs font-semibold truncate ${
                      isSelected ? 'text-emerald-500 dark:text-emerald-400 font-bold' : ''
                    }`}
                  >
                    {bg.label}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div
          className={`mt-4 pt-3 border-t border-inherit flex items-center justify-between text-xs ${
            isDark ? 'text-slate-400' : 'text-slate-500'
          }`}
        >
          <span className="truncate">Auto-adapts to landscape & portrait</span>
          {selectedBgId !== activeDefaultId && (
            <button
              type="button"
              onClick={() => onSelectBackground(activeDefaultId)}
              className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium shrink-0 ml-2"
            >
              Reset to Default
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
}
